import { useEffect, useRef } from 'react';
import { useSession } from './session.tsx';
import { offlineSessionIdentity } from './offline-cache.ts';
import type { InboxActionKind } from '../../shared/inbox-actions.ts';

export function advertisedInboxKinds(company: unknown): InboxActionKind[] {
  const value = (company as { inboxActionKinds?: unknown } | null)?.inboxActionKinds;
  return Array.isArray(value)
    ? value.filter((kind): kind is InboxActionKind =>
        ['reschedule', 'visit_instruction', 'create_quote'].includes(kind),
      )
    : ['reschedule'];
}

export function useInboxKindScope(target: string) {
  const session = useSession();
  const kinds = advertisedInboxKinds(session.company);
  const identity = JSON.stringify([
    offlineSessionIdentity(),
    session.user?.id,
    session.user?.role,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.permissions,
    session.company?.accessLocked,
    session.company?.inboxActionsAvailable,
    session.company?.planFeatures,
    kinds,
    session.impersonatedBy,
    (
      ['customers:read', 'customers:write', 'schedule:read', 'schedule:write', 'quotes:read', 'quotes:write'] as const
    ).map(session.can),
  ]);
  const scope = useRef({ identity, target, epoch: 0 });
  if (scope.current.identity !== identity || scope.current.target !== target)
    scope.current = { identity, target, epoch: scope.current.epoch + 1 };
  const captured = scope.current;
  // Canonical cache receipts may complete after navigation. Their witness follows the
  // shared monotonic authorization/API scope, independently of this component's life.
  const authorization = useRef({ identity });
  if (authorization.current.identity !== identity) authorization.current = { identity };
  const capturedAuthorization = authorization.current;
  const receiptCurrent = () =>
    authorization.current === capturedAuthorization && offlineSessionIdentity() === JSON.parse(identity)[0];
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const current = () =>
    active.current && scope.current === captured && offlineSessionIdentity() === JSON.parse(identity)[0];
  const allowed = Boolean(
    session.user &&
    session.company?.inboxActionsAvailable &&
    !session.company.accessLocked &&
    session.company.planFeatures.includes('sms') &&
    session.user.role !== 'crew' &&
    !session.impersonatedBy &&
    session.can('customers:read'),
  );
  const canRead = (kind: InboxActionKind) =>
    allowed && kinds.includes(kind) && session.can(kind === 'create_quote' ? 'quotes:read' : 'schedule:read');
  const canDecide = (kind: InboxActionKind) =>
    canRead(kind) &&
    session.can('customers:write') &&
    session.can(kind === 'create_quote' ? 'quotes:write' : 'schedule:write');
  return {
    identity: `${identity}:${captured.epoch}`,
    disclosureIdentity: identity,
    current,
    receiptCurrent,
    allowed,
    canRead,
    canDecide,
    kinds,
  };
}
